import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { INITIALIZE_STORYBOARD_IMAGE_GENERATION } from '../../../../graphql/INITIALIZE_STORYBOARD_IMAGE_GENERATION';
import { FETCH_STORYBOARD_IMAGE_GENERATION_PROGRESS } from '../../../../graphql/FETCH_STORYBOARD_IMAGE_GENERATION_PROGRESS';
import { CHECK_HAS_ACCESS_TO_STORYBOARD_IMAGE_GENERATION_FEATURE } from '../../../../graphql/CHECK_HAS_ACCESS_TO_STORYBOARD_IMAGE_GENERATION_FEATURE';
import {
  uploadMultipleImagesToS3,
  splitImageIntoQuadrants
} from '../../../Upload';
import { Media, GeneratedImagePool } from '../ProjectFlow';
import mascotHappy from '../../../../assets/svg/onboarding/mascot-happy.svg';
import { PRESIGN_LINK } from '../../../../graphql/PRESIGN_LINK';
import { SAVE_PRESIGNED_LINK } from '../../../../graphql/SAVE_PRESIGNED_LINK';

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (
    medias: Media[],
    mode: 'ai' | 'manual',
    aiPool?: GeneratedImagePool
  ) => void;
  projectIdea: string;
  existingMode?: 'ai' | 'manual' | null;
  existingMedias?: Media[];
  existingAIPool?: GeneratedImagePool;
}

// 8 seconds polling
const POLL_INTERVAL_IN_MILLISECONDS = 8000;
const formSchema = Yup.object().shape({
  projectIdea: Yup.string()
    .required('This field is required')
    .min(5, 'At least 5 characters')
});
const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  projectIdea,
  existingMode,
  existingMedias,
  existingAIPool
}) => {
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [showManualChoiceModal, setShowManualChoiceModal] = useState(false);
  const [aiGeneratedImages, setAIGeneratedImages] = useState<Media[]>(
    existingAIPool?.images ?? []
  );
  const [selectedAIImageIds, setSelectedAIImageIds] = useState<string[]>(
    existingAIPool?.selectedIds ?? []
  );
  const [manualUploadedImages, setManualUploadedImages] = useState<Media[]>(
    existingMode === 'manual' ? existingMedias ?? [] : []
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [messageId, setMessageId] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const modalScrollRef = React.useRef<HTMLDivElement>(null);
  const [preSignLink] = useMutation(PRESIGN_LINK);
  const [savePreSignedLink] = useMutation(SAVE_PRESIGNED_LINK);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isUploading, setIsUploading] = useState(false);

  const [tempOldAIImages, setTempOldAIImages] = useState<Media[]>([]);
  const [tempOldAISelectedIds, setTempOldAISelectedIds] = useState<string[]>(
    []
  );

  const { loading: isCheckingAccess, data: accessData } = useQuery(
    CHECK_HAS_ACCESS_TO_STORYBOARD_IMAGE_GENERATION_FEATURE
  );

  const hasNoAccessToAI =
    !isCheckingAccess &&
    !accessData?.checkHasAccessToStoryboardImageGenerationFeature?.access;

  const [generateStoryboardMutation] = useMutation(
    INITIALIZE_STORYBOARD_IMAGE_GENERATION,
    {
      onCompleted: (res) => {
        const msgId = res?.initializeStoryboardImageGeneration?.messageId;
        if (msgId && typeof msgId === 'string') {
          setMessageId(msgId);
        }
      },
      onError: () => toast.error('Failed to generate image')
    }
  );

  const handleUploadManually = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAIImageComplete = (
    selectedMedias: Media[],
    allMedias: Media[]
  ) => {
    const pool: GeneratedImagePool = {
      messageId,
      images: allMedias,
      selectedIds: selectedMedias.map((m) => m.id),
      generatedAt: Date.now()
    };

    onComplete(selectedMedias, 'ai', pool);
    setMessageId('');
    setTempOldAIImages([]);
    setTempOldAISelectedIds([]);
  };

  const handleGenerateAIImages = async () => {
    setIsGenerating(true);

    try {
      const result = await generateStoryboardMutation({
        variables: { scenario: projectIdea }
      });

      const msgId =
        result?.data?.initializeStoryboardImageGeneration?.messageId;
      if (msgId && typeof msgId === 'string') {
        setMessageId(msgId);
        setIsGenerating(false);
      } else {
        toast.error('Failed to start image generation');
        setIsGenerating(false);
      }
    } catch (error) {
      toast.error('Failed to generate images');
      setIsGenerating(false);
    }
  };

  const toggleAIImageSelection = (imageId: string) => {
    setSelectedAIImageIds(
      (
        prev // ✅ Single call
      ) =>
        prev.includes(imageId)
          ? prev.filter((id) => id !== imageId)
          : [...prev, imageId]
    );
  };

  const handleRegenerateAI = () => {
    setShowRegenerateConfirm(true);
  };

  const confirmRegenerate = () => {
    setTempOldAIImages(aiGeneratedImages);
    setTempOldAISelectedIds(selectedAIImageIds);

    setAIGeneratedImages([]);
    setSelectedAIImageIds([]);
    setShowRegenerateConfirm(false);
  };

  const cancelRegenerate = () => {
    setShowRegenerateConfirm(false);
  };

  const handleManualUpload = async (files: File[]) => {
    const maxImages = 4;
    const currentCount = manualUploadedImages.length;
    const remainingSlots = maxImages - currentCount;

    if (files.length > remainingSlots) {
      toast.warn(`You can only add ${remainingSlots} more images`);
      return;
    }

    setIsUploading(true);

    try {
      const uploadedMedias = await uploadMultipleImagesToS3(
        files,
        preSignLink,
        savePreSignedLink
      );
      const allImages = [...manualUploadedImages, ...uploadedMedias];
      onComplete(allImages, 'manual');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveManualImage = (indexToRemove: number) => {
    setManualUploadedImages((prev) =>
      prev.filter((_, idx) => idx !== indexToRemove)
    );
  };

  const handleContinueManual = () => {
    if (manualUploadedImages.length === 0) {
      toast.error('Please upload at least 1 image');
      return;
    }

    onComplete(manualUploadedImages, 'manual');
  };

  const handleContinueWithAISelection = () => {
    if (selectedAIImageIds.length === 0) {
      toast.error('Please select at least 1 image');
      return;
    }

    const selectedMedias = aiGeneratedImages.filter((img) =>
      selectedAIImageIds.includes(img.id)
    );

    const pool: GeneratedImagePool = {
      messageId: existingAIPool?.messageId ?? '',
      images: aiGeneratedImages,
      selectedIds: selectedAIImageIds,
      generatedAt: existingAIPool?.generatedAt ?? Date.now()
    };

    onComplete(selectedMedias, 'ai', pool);
  };

  const handleBack = () => {
    setMessageId('');
  };

  const handleSkip = () => {
    if (existingMedias && existingMedias.length > 0) {
      onClose();
    } else {
      setTempOldAIImages([]);
      setTempOldAISelectedIds([]);
      onComplete([], 'manual');
    }
  };

  const handleCancelFromGeneration = () => {
    if (tempOldAIImages.length > 0) {
      setAIGeneratedImages(tempOldAIImages);
      setSelectedAIImageIds(tempOldAISelectedIds);
      setTempOldAIImages([]);
      setTempOldAISelectedIds([]);
    } else {
      onClose();
    }
  };

  const handleContinueWithExisting = () => {
    setShowManualChoiceModal(false);
    onComplete(manualUploadedImages, 'manual');
  };

  const handleAddMoreManual = () => {
    setShowManualChoiceModal(false);
  };

  const handleStartOver = () => {
    setShowManualChoiceModal(false);
    setManualUploadedImages([]);
  };

  const initialValues = {
    projectIdea: projectIdea ?? ''
  };

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if ((event.key === 'Escape' || event.key === 'Esc') && !messageId) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, messageId, onClose]);

  useEffect(() => {
    if (isOpen) {
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        setTimeout(() => {
          if (modalScrollRef.current) {
            const modal = modalScrollRef.current;
            const modalRect = modal.getBoundingClientRect();
            const modalHeight = modalRect.height;
            const windowHeight = window.innerHeight;

            const modalTop = modalRect.top + window.pageYOffset;
            const scrollPosition = modalTop - (windowHeight - modalHeight) / 2;

            window.scrollTo({
              top: Math.max(0, scrollPosition),
              behavior: 'smooth'
            });
          }
        }, 100);
      } else {
        window.scrollTo(0, 0);
        setTimeout(() => {
          if (modalScrollRef.current) {
            modalScrollRef.current.scrollTop = 0;
            const firstElement = modalScrollRef.current.querySelector('.card');
            if (firstElement) {
              firstElement.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
          }
        }, 100);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      existingMedias &&
      existingMedias.length > 0 &&
      existingMode !== 'ai'
    ) {
      setManualUploadedImages(existingMedias);
      setShowManualChoiceModal(true);
    }
  }, [isOpen, existingMode, existingMedias]);

  useEffect(() => {
    if (isOpen && existingAIPool && existingMode === 'ai') {
      setAIGeneratedImages(existingAIPool.images);
      setSelectedAIImageIds(existingAIPool.selectedIds);
    }
  }, [isOpen, existingMode, existingAIPool]);

  if (!isOpen) return null;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    await handleManualUpload(fileArray);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`modal ${isOpen ? 'is-active' : ''}`}>
      <div className="modal-background">
        <section className="container">
          <div className="columns section page-add-project-container no-bottom-margin">
            <div className="column">
              <div
                className="modal-scroll-container image-upload-modal"
                ref={modalScrollRef}
              >
                {showManualChoiceModal ? (
                  <ManualImageChoiceModal
                    imageCount={manualUploadedImages.length}
                    onContinue={handleContinueWithExisting}
                    onAddMore={handleAddMoreManual}
                    onStartOver={handleStartOver}
                  />
                ) : manualUploadedImages.length > 0 &&
                  !messageId &&
                  !showManualChoiceModal ? (
                  <ManualUploadView
                    images={manualUploadedImages}
                    onRemoveImage={handleRemoveManualImage}
                    onContinue={handleContinueManual}
                    onUploadMore={handleUploadManually}
                    onCancel={onClose}
                    isUploading={isUploading}
                    maxImages={4}
                  />
                ) : aiGeneratedImages.length > 0 && !messageId ? (
                  <ExistingAIImageSelection
                    images={aiGeneratedImages}
                    selectedIds={selectedAIImageIds}
                    onToggleSelection={toggleAIImageSelection}
                    onContinue={handleContinueWithAISelection}
                    onRegenerate={handleRegenerateAI}
                    onCancel={onClose}
                  />
                ) : !messageId ? (
                  <div className="card page-add-project-card is-rounded is-dropshadowed">
                    <Formik
                      validationSchema={formSchema}
                      initialValues={initialValues}
                      onSubmit={async (values, { setSubmitting }) => {
                        await generateStoryboardMutation({
                          variables: {
                            scenario: values.projectIdea
                          }
                        });
                        setSubmitting(false);
                      }}
                    >
                      {({
                        values,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        errors,
                        touched
                      }) => (
                        <>
                          <div className="card-content page-add-project-card-content">
                            {/* Move mascot and titles inside content */}
                            <div className="modal-header-content mb-5">
                              <div className="modal-mascot-container">
                                <img
                                  src={mascotHappy}
                                  alt="Mascot"
                                  className="modal-mascot-image"
                                />
                              </div>
                              <h1 className="modal-title">
                                Bring Your Project to Life with an Image
                              </h1>
                              <p className="modal-subtitle">
                                Upload Project Photos Or Generate Them Using AI
                              </p>
                            </div>

                            <div className="mb-4">
                              <div className="field">
                                <div className="control">
                                  <textarea
                                    name="projectIdea"
                                    className={`textarea has-background-white modal-textarea ${
                                      errors.projectIdea && touched.projectIdea
                                        ? 'is-danger'
                                        : ''
                                    }`}
                                    rows={3}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="I Need Videographers For My Film"
                                  />
                                </div>
                                {errors.projectIdea && touched.projectIdea && (
                                  <p className="help is-danger">
                                    {errors.projectIdea}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="buttons is-centered">
                              <button
                                type="button"
                                className={`button is-fullwidth is-primary has-text-weight-semibold ${
                                  isSubmitting ||
                                  isGenerating ||
                                  isCheckingAccess
                                    ? 'is-loading'
                                    : ''
                                }`}
                                onClick={handleGenerateAIImages}
                                disabled={
                                  isSubmitting ||
                                  isGenerating ||
                                  isCheckingAccess ||
                                  hasNoAccessToAI ||
                                  !values.projectIdea?.trim() ||
                                  values.projectIdea.trim().length < 5
                                }
                              >
                                Generate with AI
                              </button>
                            </div>

                            <div className="divider-with-lines">
                              <span>or</span>
                            </div>

                            <div className="buttons is-centered with-gap">
                              <button
                                type="button"
                                className={`button is-outlined is-primary has-text-weight-semibold flex-1 ${
                                  isUploading ? 'is-loading' : ''
                                }`}
                                onClick={handleUploadManually}
                                disabled={isUploading}
                              >
                                Upload Manually
                              </button>
                              {tempOldAIImages.length > 0 ? (
                                <>
                                  <button
                                    type="button"
                                    className="button is-outlined is-primary has-text-weight-semibold flex-1"
                                    onClick={handleCancelFromGeneration}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="button is-outlined is-primary has-text-weight-semibold flex-1"
                                    onClick={handleSkip}
                                  >
                                    Skip for now
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="button is-outlined is-primary has-text-weight-semibold flex-1"
                                  onClick={handleSkip}
                                >
                                  {existingMedias && existingMedias.length > 0
                                    ? 'Cancel'
                                    : 'Skip for now'}
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </Formik>
                  </div>
                ) : (
                  <AIImageGeneration
                    messageId={messageId}
                    onComplete={handleAIImageComplete}
                    onCancel={handleBack}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {showRegenerateConfirm && (
        <div className="modal is-active regenerate-confirm-modal">
          <div className="modal-background" onClick={cancelRegenerate}></div>

          <div className="modal-content">
            <div className="box">
              <div className="regenerate-confirm-modal-header">
                <p className="regenerate-confirm-modal-title">
                  Go back to image selection?
                </p>
                <p className="regenerate-confirm-modal-subtitle">
                  Current selection will be cleared.
                </p>
              </div>

              <div className="selection-modal-buttons">
                <button
                  type="button"
                  className="button page-add-project-controls-button has-text-weight-semibold is-outlined"
                  onClick={cancelRegenerate}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button page-add-project-controls-button has-text-weight-semibold is-primary"
                  onClick={confirmRegenerate}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AIImageGeneration = ({
  messageId,
  onComplete,
  onCancel
}: {
  messageId: string;
  onComplete: (selectedMedias: Media[], allMedias: Media[]) => void;
  onCancel: () => void;
}) => {
  const [imageUrl, setImageUrl] = useState('');
  const [currentProgress, setCurrentProgress] = useState<number | null>(null);
  const [selectedImageNos, setSelectedImageNos] = useState<number[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [preSignLink] = useMutation(PRESIGN_LINK);
  const [savePreSignedLink] = useMutation(SAVE_PRESIGNED_LINK);

  const { data, stopPolling, error } = useQuery(
    FETCH_STORYBOARD_IMAGE_GENERATION_PROGRESS,
    {
      variables: { messageId },
      pollInterval: POLL_INTERVAL_IN_MILLISECONDS,
      onError: () => {
        stopPolling();
        toast.error('Failed to generate the image');
      }
    }
  );

  const handleQuadrantClick = (value: number) => {
    if (selectedImageNos.includes(value)) {
      setSelectedImageNos(selectedImageNos.filter((v) => v !== value));
    } else {
      setSelectedImageNos([...selectedImageNos, value]);
    }
  };

  useEffect(() => {
    const progress = data?.fetchStoryboardImageGenerationProgress?.progress;
    const uri = data?.fetchStoryboardImageGenerationProgress?.uri;

    if (typeof progress === 'number') {
      setCurrentProgress(progress);
    }
    if (typeof uri === 'string') {
      setImageUrl(uri);
    }
    if (progress === 100) {
      stopPolling();
    }
  }, [data]);

  const uploadImage = async (): Promise<void> => {
    if (!imageUrl || selectedImageNos.length === 0) return;

    setIsUploading(true);

    try {
      const allQuadrantFiles = await splitImageIntoQuadrants(
        imageUrl,
        [1, 2, 3, 4]
      );
      const allUploadedMedias = await uploadMultipleImagesToS3(
        allQuadrantFiles,
        preSignLink,
        savePreSignedLink
      );
      const selectedMedias = allUploadedMedias.filter((_, idx) =>
        selectedImageNos.includes(idx + 1)
      );

      onComplete(selectedMedias, allUploadedMedias);
    } catch (error) {
      console.error('Error uploading quadrants:', error);
      toast.error('Failed to upload quadrants');
    } finally {
      setIsUploading(false);
    }
  };

  if (currentProgress === null || currentProgress < 100) {
    return <LoadingModal progress={currentProgress ?? 0} />;
  }

  if (currentProgress === 100 && imageUrl) {
    return (
      <ImageSelectionModal
        imageUrl={imageUrl}
        selectedImageNos={selectedImageNos}
        onQuadrantClick={handleQuadrantClick}
        onCancel={onCancel}
        onContinue={uploadImage}
        isUploading={isUploading}
        error={error}
      />
    );
  }

  return null;
};

// Loading Modal Component
const LoadingModal: React.FC<{ progress: number }> = ({ progress }) => {
  return (
    <div className="card page-add-project-card is-rounded is-dropshadowed">
      {/* Loading content */}
      <div className="loading-modal-content">
        <div className="loading-modal-header">
          <div className="loading-modal-mascot-container">
            {/* Spinning circle wrapper */}
            <div className="loading-spinner-wrapper">
              {/* SVG spinning circle */}
              <svg className="loading-spinner-circle" viewBox="0 0 100 100">
                <circle
                  className="spinner-track"
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  strokeWidth="4"
                />
                <circle
                  className="spinner-progress"
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
              {/* Mascot in center */}
              <img
                src={mascotHappy}
                alt="Mascot"
                className="loading-modal-mascot"
              />
            </div>
          </div>
          <h1 className="loading-modal-title">Magic in Progress...</h1>
          <p className="loading-modal-subtitle">
            Our AI is crafting your unique storyboard. This might take a couple
            of minutes, but great things are worth the wait!
          </p>
        </div>
      </div>
    </div>
  );
};

// Image Selection Modal Component
const ImageSelectionModal: React.FC<{
  imageUrl: string;
  selectedImageNos: number[];
  onQuadrantClick: (value: number) => void;
  onCancel: () => void;
  onContinue: () => void;
  isUploading: boolean;
  error: any;
}> = ({
  imageUrl,
  selectedImageNos,
  onQuadrantClick,
  onCancel,
  onContinue,
  isUploading,
  error
}) => {
  return (
    <>
      <div className="card selection-modal-header-card is-rounded is-dropshadowed">
        <div className="selection-modal-header-section">
          {/* Progress bar at top */}
          <div className="selection-modal-progress-bar-container">
            <div className="selection-modal-progress-bar width-50" />
          </div>

          {/* Progress indicator */}
          <div className="selection-modal-progress-indicator">
            1/2 Completed
          </div>

          {/* Header content */}
          <div className="selection-modal-header-horizontal">
            <div className="selection-modal-header-left">
              <h1 className="selection-modal-title">
                Choose Your Project Image
              </h1>
              <p className="selection-modal-subtitle">
                Matchi Generated A Few Looks For You! Choose The One That Feels
                Right
              </p>
            </div>
            <div className="selection-modal-header-right">
              <img
                src={mascotHappy}
                alt="Mascot"
                className="selection-modal-mascot"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content Card with Image Grid */}
      <div className="card selection-modal-header-card is-rounded is-dropshadowed">
        <div className="selection-modal-content">
          <div className="selection-modal-image-wrapper">
            {/* Single image displayed once */}
            <img
              src={imageUrl}
              alt="Generated storyboard options"
              className="selection-modal-full-image"
            />

            {/* Transparent overlay grid for selection */}
            <div className="selection-modal-overlay-grid">
              {[
                { value: 1, label: 'Top Left' },
                { value: 2, label: 'Top Right' },
                { value: 3, label: 'Bottom Left' },
                { value: 4, label: 'Bottom Right' }
              ].map((quadrant) => (
                <div
                  key={quadrant.value}
                  className={`selection-modal-quadrant ${
                    selectedImageNos.includes(quadrant.value) ? 'selected' : ''
                  }`}
                  onClick={() => onQuadrantClick(quadrant.value)}
                >
                  <div className="selection-modal-quadrant-checkmark" />
                </div>
              ))}
            </div>
          </div>

          {/* Buttons inside content card */}
          <div className="selection-modal-buttons">
            <button
              type="button"
              className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold"
              onClick={onCancel}
              disabled={isUploading}
            >
              Cancel
            </button>
            {!error && (
              <button
                type="button"
                className={`button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary ${
                  isUploading ? 'is-loading' : ''
                }`}
                onClick={onContinue}
                disabled={isUploading || selectedImageNos.length === 0}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Component to show when user returns to edit with existing AI images
const ExistingAIImageSelection: React.FC<{
  images: Media[];
  selectedIds: string[];
  onToggleSelection: (imageId: string) => void;
  onContinue: () => void;
  onRegenerate: () => void;
  onCancel: () => void;
}> = ({
  images,
  selectedIds,
  onToggleSelection,
  onContinue,
  onRegenerate,
  onCancel
}) => {
  return (
    <>
      <div className="card selection-modal-header-card is-rounded is-dropshadowed">
        <div className="selection-modal-header-section">
          <div className="selection-modal-progress-bar-container">
            <div className="selection-modal-progress-bar width-50" />
          </div>

          <div className="selection-modal-progress-indicator">
            1/2 Completed
          </div>

          <div className="selection-modal-header-horizontal">
            <div className="selection-modal-header-left">
              <h1 className="selection-modal-title">
                Edit Your Project Images
              </h1>
              <p className="selection-modal-subtitle">
                Select or deselect images from your previously generated set
              </p>
            </div>
            <div className="selection-modal-header-right">
              <img
                src={mascotHappy}
                alt="Mascot"
                className="selection-modal-mascot"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card selection-modal-header-card is-rounded is-dropshadowed">
        <div className="selection-modal-content">
          <div className="selection-modal-image-wrapper">
            <div className="selection-modal-reconstructed-grid">
              {images.map((image, index) => (
                <img
                  key={image.id}
                  src={image.fileUrl}
                  alt={`Generated ${index + 1}`}
                />
              ))}
            </div>

            <div className="selection-modal-overlay-grid">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className={`selection-modal-quadrant ${
                    selectedIds.includes(image.id) ? 'selected' : ''
                  }`}
                  onClick={() => onToggleSelection(image.id)}
                >
                  <div className="selection-modal-quadrant-checkmark" />
                </div>
              ))}
            </div>
          </div>

          <div className="selection-modal-buttons">
            <button
              type="button"
              className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-outlined"
              onClick={onRegenerate}
            >
              Generate New
            </button>
            <button
              type="button"
              className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary"
              onClick={onContinue}
              disabled={selectedIds.length === 0}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const ManualImageChoiceModal: React.FC<{
  imageCount: number;
  onContinue: () => void;
  onAddMore: () => void;
  onStartOver: () => void;
}> = ({ imageCount, onContinue, onAddMore, onStartOver }) => {
  return (
    <div className="card selection-modal-header-card is-rounded is-dropshadowed">
      <div className="selection-modal-content">
        <div className="modal-header-content mb-5">
          <div className="modal-mascot-container">
            <img
              src={mascotHappy}
              alt="Mascot"
              className="modal-mascot-image"
            />
          </div>
          <h1 className="modal-title">
            You have {imageCount} uploaded image{imageCount !== 1 ? 's' : ''}
          </h1>
          <p className="modal-subtitle">What would you like to do?</p>
        </div>

        <div className="selection-modal-buttons">
          <button
            type="button"
            className="button page-add-project-controls-button has-text-weight-semibold is-outlined"
            onClick={onStartOver}
          >
            Start over
          </button>
          <button
            type="button"
            className="button page-add-project-controls-button has-text-weight-semibold is-primary"
            onClick={onContinue}
          >
            Continue with current images
          </button>
        </div>
      </div>
    </div>
  );
};

const ManualUploadView: React.FC<{
  images: Media[];
  onRemoveImage: (index: number) => void;
  onContinue: () => void;
  onUploadMore: () => void;
  onCancel: () => void;
  isUploading: boolean;
  maxImages: number;
}> = ({
  images,
  onRemoveImage,
  onContinue,
  onUploadMore,
  onCancel,
  isUploading,
  maxImages
}) => {
  const canUploadMore = images.length < maxImages;

  return (
    <>
      <div className="card selection-modal-header-card is-rounded is-dropshadowed">
        <div className="selection-modal-header-section">
          <div className="selection-modal-progress-bar-container">
            <div className="selection-modal-progress-bar width-50" />
          </div>

          <div className="selection-modal-progress-indicator">
            1/2 Completed
          </div>

          <div className="selection-modal-header-horizontal">
            <div className="selection-modal-header-left">
              <h1 className="selection-modal-title">Your Uploaded Images</h1>
              <p className="selection-modal-subtitle">
                {images.length} of {maxImages} images uploaded. Add more or
                continue to next step.
              </p>
            </div>
            <div className="selection-modal-header-right">
              <img
                src={mascotHappy}
                alt="Mascot"
                className="selection-modal-mascot"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card selection-modal-header-card is-rounded is-dropshadowed">
        <div className="selection-modal-content">
          <div className="manual-upload-grid">
            {images.map((image, index) => (
              <div key={index} className="manual-upload-image-container">
                <img
                  src={image.fileUrl}
                  alt={`Upload ${index + 1}`}
                  className="manual-upload-image"
                />
                <button
                  type="button"
                  className="manual-upload-remove-button"
                  onClick={() => onRemoveImage(index)}
                  disabled={isUploading}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="selection-modal-buttons">
            <button
              type="button"
              className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-outlined"
              onClick={onCancel}
              disabled={isUploading}
            >
              Cancel
            </button>
            {canUploadMore && (
              <button
                type="button"
                className={`button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-outlined ${
                  isUploading ? 'is-loading' : ''
                }`}
                onClick={onUploadMore}
                disabled={isUploading}
              >
                Upload More
              </button>
            )}
            <button
              type="button"
              className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary"
              onClick={onContinue}
              disabled={isUploading || images.length === 0}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImageUploadModal;
